import React, { useState, useEffect, useCallback } from 'react';
import StatementsUpload from '../components/StatementsUpload';
import api from '../services/api';

// Вспомогательные функции форматирования
const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }
    return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(value));
};

const formatDateToRussian = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
};

const formatAddress = (address) => {
    if (!address) return '-';
    const parts = [];
    if (address.city) parts.push(address.city);
    if (address.street) parts.push(address.street);
    if (address.home) parts.push(`д. ${address.home}`);
    if (address.apartment) parts.push(`кв. ${address.apartment}`);
    return parts.join(', ');
};

// Модальное окно назначения приказа
const AssignmentModal = ({ statement, onClose, onUpdate }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);

    // Фильтры
    const [filters, setFilters] = useState({
        caseNumber: '',
        personName: '',
        accountNumber: '',
        sourceDocumentNumber: '',
        address: ''
    });

    // Пагинация
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 1
    });

    // Загрузка списка приказов
    const loadOrders = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                pageSize: pagination.pageSize.toString(),
                ...filters
            });

            const response = await api.getOrders(params);
            
            // Адаптация под возможный формат ответа
            const dataList = response.data?.data || response.data || [];
            const totalCount = response.data?.totalCount || 0;
            const totalPages = response.data?.totalPages || (dataList.length > 0 ? 1 : 0);

            setOrders(dataList);
            setPagination(prev => ({
                ...prev,
                totalCount,
                totalPages
            }));
        } catch (error) {
            console.error('Error loading orders:', error);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.pageSize, filters]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    // Обработчик изменения фильтров
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, page: 1 })); // Сброс на 1 страницу
    };

    const clearFilters = () => {
        setFilters({
            caseNumber: '',
            personName: '',
            accountNumber: '',
            sourceDocumentNumber: '',
            address: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Навигация по страницам
    const goToPage = (page) => {
        if (page >= 1 && (pagination.totalPages === 0 || page <= pagination.totalPages)) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    // Назначение приказа
    const handleAssign = async (orderId) => {
        if (!window.confirm('Назначить этот приказ данной выписке?')) return;
        
        setAssigning(true);
        try {
            // Предполагаемый метод API для привязки
            await api.linkStatementToOrder(statement.id, orderId);
            onUpdate(); // Обновляем список в родительском компоненте
            onClose();  // Закрываем модалку
        } catch (error) {
            console.error('Error assigning order:', error);
            alert('Ошибка при назначении приказа');
        } finally {
            setAssigning(false);
        }
    };

    // Отмена приказа
    const handleUnassign = async () => {
        if (!window.confirm('Вы уверены, что хотите отменить связь с текущим приказом?')) return;

        setAssigning(true);
        try {
            // Предполагаемый метод API для отвязки
            await api.unlinkStatementFromOrder(statement.id);
            onUpdate();
            onClose();
        } catch (error) {
            console.error('Error unassigning order:', error);
            alert('Ошибка при отмене назначения');
        } finally {
            setAssigning(false);
        }
    };

    const generatePageNumbers = () => {
        const pages = [];
        const current = pagination.page;
        const total = pagination.totalPages;
        if (total <= 5) {
            for (let i = 1; i <= total; i++) pages.push(i);
        } else {
            pages.push(1);
            if (current > 3) pages.push('...');
            const start = Math.max(2, current - 1);
            const end = Math.min(total - 1, current + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (current < total - 2) pages.push('...');
            pages.push(total);
        }
        return pages;
    };

    // Рендеринг пагинации (упрощенная версия для модалки)
    const renderPagination = () => (
        <div className="pagination-controls" style={{ justifyContent: 'center', marginTop: '10px' }}>
             <button onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page === 1} className="btn-pagination">⟨</button>
             {generatePageNumbers().map((p, i) => (
                 p === '...' ? <span key={i} className="pagination-ellipsis">...</span> :
                 <button key={i} onClick={() => goToPage(p)} className={`btn-pagination ${pagination.page === p ? 'btn-pagination-active' : ''}`}>{p}</button>
             ))}
             <button onClick={() => goToPage(pagination.page + 1)} disabled={pagination.page === pagination.totalPages} className="btn-pagination">⟩</button>
        </div>
    );

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content-large" onClick={e => e.stopPropagation()} style={{ maxWidth: '1200px', height: '90vh', display: 'flex', flexDirection: 'column' }}>
                
                {/* Заголовок и текущий статус */}
                <div className="modal-header" style={{ alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                            <h3>Назначение приказа</h3>
                            <div className="current-assignment" style={{ fontSize: '1.1em', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span>Назначенный приказ:</span>
                                <strong style={{ color: statement.orderObj ? '#2980b9' : '#7f8c8d' }}>
                                    {statement.orderObj 
                                        ? `${statement.orderObj.caseNumber || 'Без номера'} (${statement.orderObj.person?.name || 'Без имени'})`
                                        : 'Нет'
                                    }
                                </strong>
                                {statement.orderObj && (
                                    <button 
                                        onClick={handleUnassign} 
                                        className="btn-delete-modal" 
                                        disabled={assigning}
                                        style={{ padding: '4px 8px', fontSize: '0.9em' }}
                                    >
                                        Отменить
                                    </button>
                                )}
                            </div>
                        </div>
                        {statement.destination && (
                            <div style={{ marginTop: '8px', fontSize: '0.95em', color: '#555', background: '#f0f4f8', borderRadius: '4px', padding: '6px 10px' }}>
                                <span style={{ fontWeight: '600', color: '#333' }}>Назначение выписки: </span>
                                {statement.destination}
                            </div>
                        )}
                    </div>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                {/* Область фильтров */}
                <div className="filters-panel" style={{ margin: '15px 0', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <div className="filters-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                        <div className="filter-group">
                            <label>№ Приказа</label>
                            <input
                                type="text"
                                value={filters.caseNumber}
                                onChange={(e) => handleFilterChange('caseNumber', e.target.value)}
                                placeholder="Поиск..."
                            />
                        </div>
                        <div className="filter-group">
                            <label>ФИО</label>
                            <input
                                type="text"
                                value={filters.personName}
                                onChange={(e) => handleFilterChange('personName', e.target.value)}
                                placeholder="Поиск..."
                            />
                        </div>
                        <div className="filter-group">
                            <label>Номер счёта</label>
                            <input
                                type="text"
                                value={filters.accountNumber}
                                onChange={(e) => handleFilterChange('accountNumber', e.target.value)}
                                placeholder="Поиск..."
                            />
                        </div>
                        <div className="filter-group">
                            <label>№ ИД</label>
                            <input
                                type="text"
                                value={filters.sourceDocumentNumber}
                                onChange={(e) => handleFilterChange('sourceDocumentNumber', e.target.value)}
                                placeholder="Поиск..."
                            />
                        </div>
                        <div className="filter-group">
                            <label>Город</label>
                            <input
                                type="text"
                                value={filters.address}
                                onChange={(e) => handleFilterChange('address', e.target.value)}
                                placeholder="Поиск..."
                            />
                        </div>
                    </div>
                    <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={clearFilters} className="btn-secondary" style={{ fontSize: '0.9em' }}>
                            Очистить фильтры
                        </button>
                    </div>
                </div>

                {/* Таблица приказов */}
                <div className="table-wrapper" style={{ flex: 1, overflow: 'auto' }}>
                    {loading ? (
                        <div className="loading">Загрузка приказов...</div>
                    ) : orders.length === 0 ? (
                        <div className="no-data">Приказы не найдены</div>
                    ) : (
                        <table className="statements-table" style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>№ Приказа</th>
                                    <th>ФИО</th>
                                    <th>Номер счёта</th>
                                    <th>№ ИД</th>
                                    <th>Адрес</th>
                                    <th style={{ width: '100px', textAlign: 'center' }}>Действие</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map(order => (
                                    <tr key={order.id}>
                                        <td>{order.caseNumber}</td>
                                        <td>{order.person?.name || '-'}</td>
                                        <td>{order.accountNumber}</td>
                                        <td>{order.sourceDocumentNumber}</td>
                                        <td>{formatAddress(order.address)}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleAssign(order.id)}
                                                className="btn-primary"
                                                disabled={assigning}
                                                style={{ padding: '4px 10px' }}
                                            >
                                                Назначить
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Пагинация снизу */}
                <div className="pagination-bottom">
                    <div className="pagination-info">
                        Записи {pagination.totalCount > 0 ? (pagination.page - 1) * pagination.pageSize + 1 : 0} - {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} из {pagination.totalCount}
                    </div>
                    {renderPagination()}
                </div>
            </div>
        </div>
    );
};

// Компонент для отображения списка выписок
const StatementsList = ({ type, onSaveSuccess }) => {
    const [statements, setStatements] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Состояние для модального окна
    const [selectedStatement, setSelectedStatement] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Состояние для выгрузки в Excel
    const [exporting, setExporting] = useState(false);

    // Состояние для удаления всех неопределённых выписок
    const [deletingAll, setDeletingAll] = useState(false);

    // Состояние для удаления всех загруженных выписок с приказами
    const [deletingAllocated, setDeletingAllocated] = useState(false);

    // Состояние пагинации
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 25,
        totalCount: 0,
        totalPages: 1
    });

    // Сброс страницы при переключении типа таблицы
    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1, totalPages: 1, totalCount: 0 }));
    }, [type]);

    // Загрузка данных
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            let response;
            // Вызов соответствующих методов API
            if (type === 'uploaded') {
                response = await api.getStatementsUploadedWithOrders(pagination.page, pagination.pageSize);
            } else {
                response = await api.getStatementsUnallocated(pagination.page, pagination.pageSize);
            }

            // Обработка ответа
            const dataList = response.data?.data || response.data || [];
            const totalCount = response.data?.totalCount || 0;
            const totalPages = response.data?.totalPages || (dataList.length > 0 ? 1 : 0);

            setStatements(dataList);
            setPagination(prev => ({
                ...prev,
                totalCount: totalCount,
                totalPages: totalPages
            }));

        } catch (error) {
            console.error('Error loading statements:', error);
            alert('Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    }, [type, pagination.page, pagination.pageSize]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Обработчик двойного клика
    const handleRowDoubleClick = (statement) => {
        setSelectedStatement(statement);
        setShowModal(true);
    };

    // Запись загруженных выписок в базу
    const [saving, setSaving] = useState(false);

    const handleSaveToDB = async () => {
        if (!window.confirm('Записать все загруженные выписки в базу?')) return;

        setSaving(true);
        try {
            await api.saveUploadedStatementsToDB();
            await loadData();
            if (onSaveSuccess) onSaveSuccess();
        } catch (error) {
            console.error('Error saving statements to DB:', error);
            alert('Ошибка при записи выписок в базу');
        } finally {
            setSaving(false);
        }
    };

    // Удаление всех загруженных выписок с приказами
    const handleDeleteAllAllocated = async () => {
        if (!window.confirm('Вы уверены, что хотите удалить ВСЕ загруженные выписки с приказами? Это действие необратимо.')) return;

        setDeletingAllocated(true);
        try {
            await api.deleteAllStatementsAllocated();
            await loadData();
            if (onSaveSuccess) onSaveSuccess();
        } catch (error) {
            console.error('Delete all allocated error:', error);
            alert('Ошибка при удалении выписок');
        } finally {
            setDeletingAllocated(false);
        }
    };

    // Удаление всех неопределённых выписок
    const handleDeleteAll = async () => {
        if (!window.confirm('Вы уверены, что хотите удалить ВСЕ неопределённые выписки? Это действие необратимо.')) return;

        setDeletingAll(true);
        try {
            await api.deleteAllStatementsUnallocated();
            await loadData();
            // Обновляем счётчики после удаления всех неопределённых выписок
            if (onSaveSuccess) onSaveSuccess();
        } catch (error) {
            console.error('Delete all error:', error);
            alert('Ошибка при удалении выписок');
        } finally {
            setDeletingAll(false);
        }
    };

    // Выгрузка неопределённых выписок в Excel
    const handleExportToExcel = async () => {
        setExporting(true);
        try {
            const response = await api.exportStatementsUnallocated();

            // Парсим имя файла из Content-Disposition: attachment; filename=...
            const disposition = response.headers.get('Content-Disposition') || '';
            // Запасное имя файла в формате statements_unallocated_YYYYMMDD_HHMMSS.xlsx
            const now = new Date();
            const pad = (n) => String(n).padStart(2, '0');
            const datePart = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
            const timePart = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
            let fileName = `statements_unallocated_${datePart}_${timePart}.xlsx`;
            // Сначала пробуем filename* (RFC 5987), затем filename
            const filenameStarMatch = disposition.match(/filename\*=(?:UTF-8'')?([^;\n]+)/i);
            const filenameMatch = disposition.match(/filename=["']?([^"';\n]+)["']?/i);
            if (filenameStarMatch) {
                fileName = decodeURIComponent(filenameStarMatch[1].trim());
            } else if (filenameMatch) {
                fileName = filenameMatch[1].trim();
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export error:', error);
            alert('Ошибка при выгрузке в Excel');
        } finally {
            setExporting(false);
        }
    };

    // Удаление выписки
    const handleDelete = async (e, statementId) => {
        e.stopPropagation();
        if (!window.confirm('Вы уверены, что хотите удалить эту выписку?')) return;

        try {
            await api.deleteStatement(statementId);
            await loadData();
            // Обновляем счётчики после удаления выписки
            if (onSaveSuccess) onSaveSuccess();
        } catch (error) {
            console.error('Error deleting statement:', error);
            alert('Ошибка при удалении выписки');
        }
    };

    // Навигация по страницам
    const goToPage = (page) => {
        if (page >= 1 && (pagination.totalPages === 0 || page <= pagination.totalPages)) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    // Генерация номеров страниц
    const generatePageNumbers = () => {
        const pages = [];
        const current = pagination.page;
        const total = pagination.totalPages;

        if (total <= 7) {
            for (let i = 1; i <= total; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (current > 3) pages.push('...');
            const start = Math.max(2, current - 1);
            const end = Math.min(total - 1, current + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (current < total - 2) pages.push('...');
            pages.push(total);
        }

        return pages;
    };

    // Рендер кнопок пагинации
    const renderPaginationControls = () => (
        <div className="pagination-controls">
            <button
                onClick={() => goToPage(1)}
                disabled={pagination.page === 1}
                className="btn-pagination btn-pagination-first"
            >
                ⟪
            </button>
            <button
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-pagination btn-pagination-prev"
            >
                ⟨
            </button>

            {generatePageNumbers().map((pageNum, index) => (
                pageNum === '...' ? (
                    <span key={index} className="pagination-ellipsis">...</span>
                ) : (
                    <button
                        key={index}
                        onClick={() => goToPage(pageNum)}
                        className={`btn-pagination ${pagination.page === pageNum ? 'btn-pagination-active' : 'btn-pagination-inactive'}`}
                    >
                        {pageNum}
                    </button>
                )
            ))}

            <button
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages || pagination.totalPages === 0}
                className="btn-pagination btn-pagination-next"
            >
                ⟩
            </button>
            <button
                onClick={() => goToPage(pagination.totalPages)}
                disabled={pagination.page === pagination.totalPages || pagination.totalPages === 0}
                className="btn-pagination btn-pagination-last"
            >
                ⟫
            </button>
        </div>
    );

    return (
        <div className="statements-list-container">
            {/* Кнопки "Выгрузить в Excel" и "Удалить все" — только для раздела "неопределённые" */}
            {type === 'unalocated' && (
                <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                    <button
                        className="btn-primary"
                        onClick={handleExportToExcel}
                        disabled={exporting}
                    >
                        {exporting ? 'Выгрузка...' : '📥 Выгрузить в Excel'}
                    </button>
                    <button
                        className="btn-delete-modal"
                        onClick={handleDeleteAll}
                        disabled={deletingAll}
                    >
                        {deletingAll ? 'Удаление...' : '🗑️ Удалить все'}
                    </button>
                </div>
            )}

             {/* Кнопки "Записать в базу" и "Удалить все" — только для раздела "с приказами" */}
            {type === 'uploaded' && (
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                    <button
                        className="btn-primary"
                        onClick={handleSaveToDB}
                        disabled={saving}
                    >
                        {saving ? 'Запись...' : 'Записать в базу'}
                    </button>
                    <button
                        className="btn-delete-modal"
                        onClick={handleDeleteAllAllocated}
                        disabled={deletingAllocated}
                    >
                        {deletingAllocated ? 'Удаление...' : '🗑️ Удалить все'}
                    </button>
                </div>
            )}

            {/* Пагинация сверху */}
            <div className="pagination-top">
                <div className="pagination-info">
                    Страница {pagination.page} из {pagination.totalPages || 1}
                    {pagination.totalCount > 0 && ` (${pagination.totalCount} всего записей)`}
                </div>
                {renderPaginationControls()}
            </div>

            {loading ? (
                <div className="loading">Загрузка данных...</div>
            ) : statements.length === 0 ? (
                <div className="no-data">Нет данных для отображения</div>
            ) : (
                <div className="table-wrapper">
                    <table className="statements-table" style={{ width: '100%' }}>
                        <thead>
                            <tr style={{ height: '50px', minHeight: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                <th rowSpan="2" style={{ width: '80px', textAlign: 'center', verticalAlign: 'middle' }}>
                                    Дата
                                </th>
                                <th rowSpan="2" style={{ width: '80px', textAlign: 'center', verticalAlign: 'middle' }}>
                                    Номер
                                </th>
                                <th rowSpan="2" style={{ width: '60px', textAlign: 'center', verticalAlign: 'middle' }}>
                                    Вид операции
                                </th>
                                <th rowSpan="2" style={{ width: '250px', textAlign: 'center', verticalAlign: 'middle' }}>
                                    Контрагент
                                </th>
                                {/* <th rowSpan="2" style={{ width: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                    ИНН
                                </th> */}
                                <th rowSpan="2" style={{ width: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                    БИК
                                </th>
                                <th rowSpan="2" style={{ width: '150px', textAlign: 'center', verticalAlign: 'middle' }}>
                                    Счет
                                </th>
                                <th rowSpan="2" style={{ width: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                    Кредит
                                </th>
                                <th rowSpan="2" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                    Назначение
                                </th>
                                <th rowSpan="2" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                    Примечание
                                </th>
                                {type === 'uploaded' && (
                                    <th rowSpan="2" style={{ width: '150px', textAlign: 'center', verticalAlign: 'middle'}}>
                                        Приказ
                                    </th>
                                )}
                                <th rowSpan="2" style={{ width: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                    Действия
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {statements.map(statement => (
                                <tr 
                                    key={statement.id || Math.random()}
                                    onDoubleClick={() => handleRowDoubleClick(statement)}
                                    style={{ cursor: 'pointer' }}
                                    className="statement-row"
                                    title="Двойной клик для назначения приказа"
                                >
                                    <td>{formatDateToRussian(statement.date)}</td>
                                    <td>{statement.number}</td>
                                    <td>{statement.typeOperation}</td>
                                    <td style={{ fontSize: '0.9em' }}>{statement.counterparty}</td>
                                    {/* <td>{statement.counterpartyINN}</td> */}
                                    <td>{statement.counterpartyBIK}</td>
                                    <td>{statement.counterpartyAccount}</td>
                                    <td className="number-cell">{formatCurrency(statement.credit)}</td>
                                    <td style={{ fontSize: '0.9em' }}>{statement.destination}</td>
                                    <td style={{ fontSize: '0.9em' }}>{statement.comment}</td>
                                    {type === 'uploaded' && (
                                        <td style={{ fontWeight: '500', color: '#2980b9' }}>
                                            {statement.orderObj
                                                ? `${statement.orderObj.caseNumber || ''} ${statement.orderObj.person?.name ? `(${statement.orderObj.person.name})` : ''}`
                                                : (statement.orderNumber || '-')
                                            }
                                        </td>
                                    )}
                                    <td style={{ textAlign: 'center' }}>
                                        <button
                                            onClick={(e) => handleDelete(e, statement.id)}
                                            className="btn-delete"
                                            title="Удалить"
                                        >
                                            <span>🗑️</span>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Пагинация снизу */}
            <div className="pagination-bottom">
                <div className="pagination-info">
                    Страница {pagination.page} из {pagination.totalPages || 1}
                </div>
                {renderPaginationControls()}
            </div>

            {/* Модальное окно назначения */}
            {showModal && selectedStatement && (
                <AssignmentModal
                    statement={selectedStatement}
                    onClose={() => {
                        setShowModal(false);
                        setSelectedStatement(null);
                    }}
                    onUpdate={loadData}
                />
            )}
        </div>
    );
};

// Компонент формы выгрузки для БРИС за период
const BrisExportForm = () => {
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [exporting, setExporting] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' | 'error'

    const handleExport = async (e) => {
        e.preventDefault();
        if (!dateFrom || !dateTo) {
            setMessage('Пожалуйста, укажите период');
            setMessageType('error');
            return;
        }

        setExporting(true);
        setMessage('');
        setMessageType('');

        try {
            const response = await api.exportDetailsByPeriod(dateFrom, dateTo);

            // Получаем имя файла из заголовка Content-Disposition, если есть
            const disposition = response.headers.get('Content-Disposition') || '';
            const match = disposition.match(/filename="?([^";\n]+)"?/);
            const fileName = match ? match[1] : `details_${dateFrom}_${dateTo}.xlsx`;

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);

            setMessage(`Файл "${fileName}" успешно выгружен`);
            setMessageType('success');
        } catch (error) {
            setMessage('Ошибка при выгрузке данных');
            setMessageType('error');
            console.error('Export error:', error);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="upload-container" style={{ marginTop: '20px' }}>
            <h3 style={{ marginBottom: '16px' }}>Выгрузка для БРИС за период</h3>
            <form onSubmit={handleExport} className="upload-form">
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ minWidth: '160px' }}>
                        <label htmlFor="bris-date-from">Дата с:</label>
                        <input
                            type="date"
                            id="bris-date-from"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group" style={{ minWidth: '160px' }}>
                        <label htmlFor="bris-date-to">Дата по:</label>
                        <input
                            type="date"
                            id="bris-date-to"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group" style={{ marginBottom: '40' }}>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={exporting}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            {exporting ? 'Выгрузка...' : 'Выгрузить'}
                        </button>
                    </div>
                </div>
            </form>

            {message && (
                <div
                    className={`message ${messageType === 'error' ? 'error' : 'success'}`}
                    style={{ marginTop: '12px' }}
                >
                    {message}
                </div>
            )}
        </div>
    );
};

const Accountant = ({ user }) => {
    const [activeSection, setActiveSection] = useState('upload');
    const [counts, setCounts] = useState({
        uploaded: 0,
        unallocated: 0
    });

    // Функция загрузки счетчиков
    const fetchCounts = async () => {
        try {
            // Запрашиваем по 1 элементу только для получения totalCount из метаданных
            const [uploadedRes, unallocatedRes] = await Promise.all([
                api.getStatementsUploadedWithOrders(1, 1),
                api.getStatementsUnallocated(1, 1)
            ]);

            setCounts({
                uploaded: uploadedRes.data?.totalCount || 0,
                unallocated: unallocatedRes.data?.totalCount || 0
            });
        } catch (error) {
            console.error('Ошибка при загрузке счетчиков:', error);
        }
    };

    // Загружаем счетчики при монтировании компонента
    useEffect(() => {
        fetchCounts();
    }, []);

    // Обновляем счетчики при переключении разделов (чтобы данные были актуальны)
    useEffect(() => {
        fetchCounts();
    }, [activeSection]);

    const menuItems = [
        { key: 'upload', label: 'Загрузка выписок из Excel' },
        { key: 'uploaded', label: 'Загруженные, с приказами', count: counts.uploaded },
        { key: 'unalocated', label: 'Загруженные, неопределенные', count: counts.unallocated },
        { key: 'bris-export', label: 'Выгрузка для БРИС за период' }
    ];

    return (
        <div className="page">
            <h2>Выписки</h2>

            {/* Горизонтальная панель кнопок */}
            <div className="page-header" style={{ justifyContent: 'flex-start', gap: '15px' }}>
                {menuItems.map(item => (
                    <button
                        key={item.key}
                        onClick={() => setActiveSection(item.key)}
                        className={`btn-with-badge ${activeSection === item.key ? 'btn-primary' : 'btn-secondary'}`}
                    >
                        {item.label}
                        {item.count !== undefined && item.count > 0 && (
                            <span className="notification-badge">
                                {item.count > 99 ? '99+' : item.count}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Основной контент */}
            <div className="accountant-content" style={{ marginTop: '20px' }}>
                {activeSection === 'upload' && <StatementsUpload onUploadSuccess={fetchCounts} />}
                {activeSection === 'uploaded' && <StatementsList type="uploaded" onSaveSuccess={fetchCounts} />}
                {activeSection === 'unalocated' && <StatementsList type="unalocated" onSaveSuccess={fetchCounts} />}
                {activeSection === 'bris-export' && <BrisExportForm />}
            </div>
        </div>
    );
};

export default Accountant;